import { useState } from 'react';

import { MapPin, Tag } from 'lucide-react';

import { useLanguage } from '../../../hooks';
import { useGetAccountStatusWithLocations } from '../../../queries';
import { NestedLocation } from '../../../types/nestedLocations';
import { cn } from '../../../utils/utils';
import { SearchBar } from '../../uiComponents/CommonComponents/SearchBar';
import { LegacyLocationsTreeContent } from './LegacyLocationsTreeContent';
import { LocationSelectorContent } from './LocationSelectorContent';
import type { ToggleAllChildrenMode } from './LocationsSelectorReducer';
import { LocationsState } from './LocationsSelectorReducer';

interface LocationSelectorDropdownContentProps {
  state: LocationsState;
  onSelectLocation: (locationId: number, locationType: 'NestableLocation' | 'Location') => void;
  onSelectAll?: () => void;
  onToggleAllChildren?: (parentLocationId: number, mode: ToggleAllChildrenMode) => void;
  allowedNestableLocations?: NestedLocation[];
  multipleSelection?: boolean;
  showLegacyLocations?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  locationsBlacklist?: number[];
  maxSelectableDepth?: number;
  contentClassName?: string;
  contentContainerClassName?: string;
  customSearchNoResultsText?: string;
}

export function LocationSelectorDropdownContent({
  state,
  onSelectLocation,
  onSelectAll,
  onToggleAllChildren,
  allowedNestableLocations,
  multipleSelection = false,
  showLegacyLocations = false,
  showSearch = true,
  searchPlaceholder,
  locationsBlacklist,
  maxSelectableDepth,
  contentClassName,
  contentContainerClassName,
  customSearchNoResultsText,
}: LocationSelectorDropdownContentProps) {
  const lang = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [mode, setMode] = useState<'NestableLocation' | 'Location'>(
    state.legacySelectedLocations.length > 0 ? 'Location' : 'NestableLocation',
  );
  const { selectedLocations, legacySelectedLocations } = state;
  const { data: accountStatusWithLocations } = useGetAccountStatusWithLocations();

  const hasOldLocations = accountStatusWithLocations?.has_old_locations ?? false;

  return (
    <div
      className={cn(
        'flex flex-col space-y-2 p-4 w-full overflow-y-auto max-h-[48vh]',
        contentContainerClassName,
      )}
    >
      {showSearch && (
        <SearchBar
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={searchPlaceholder || lang.locations.searchPlaceholder}
        />
      )}

      {showLegacyLocations && hasOldLocations ? (
        <>
          <div className='flex flex-row gap-2 border-b-[2px] border-gray-300'>
            <button
              type='button'
              onClick={() => setMode('NestableLocation')}
              className={cn(
                'relative px-3 py-1.5 text-sm transition-colors flex items-center gap-2 border-b-[2px] -mb-[2px]',
                mode === 'NestableLocation'
                  ? 'text-[#3E8FFA] border-[#3E8FFA] z-10'
                  : 'text-gray-700 hover:text-gray-900 border-transparent',
              )}
            >
              <MapPin
                size={16}
                className={cn(
                  'shrink-0',
                  mode === 'NestableLocation'
                    ? 'text-[#3E8FFA] stroke-[#3E8FFA]'
                    : 'text-gray-700 stroke-gray-700',
                )}
              />
              <span>{lang.commonComponents.locationSelector.nestableLocations}</span>
            </button>
            <button
              type='button'
              onClick={() => setMode('Location')}
              className={cn(
                'relative px-3 py-1.5 text-sm transition-colors flex items-center gap-2 border-b-[2px] -mb-[2px]',
                mode === 'Location'
                  ? 'text-[#3E8FFA] font-medium border-[#3E8FFA] z-10'
                  : 'text-gray-700 hover:text-gray-900 border-transparent',
              )}
            >
              <Tag
                size={16}
                className={cn(
                  'shrink-0',
                  mode === 'Location'
                    ? 'text-[#3E8FFA] stroke-[#3E8FFA]'
                    : 'text-gray-700 stroke-gray-700',
                )}
              />
              <span>{lang.commonComponents.locationSelector.legacyLocations}</span>
            </button>
          </div>

          <div className='min-h-0 flex-1 overflow-y-auto'>
            {mode === 'Location' ? (
              <LegacyLocationsTreeContent
                searchQuery={searchQuery}
                selectedLocations={legacySelectedLocations}
                onSelect={(id) => onSelectLocation(id, 'Location')}
                multipleSelection={multipleSelection}
                customSearchNoResultsText={customSearchNoResultsText}
              />
            ) : (
              <LocationSelectorContent
                searchQuery={searchQuery}
                multipleSelection={multipleSelection}
                locationsBlacklist={locationsBlacklist}
                allowedLocations={allowedNestableLocations}
                autoSelectChildren={state.autoSelectChildren}
                selectedLocations={selectedLocations}
                allLocationsSelected={state.allLocationsSelected}
                onSelect={(id) => onSelectLocation(id, 'NestableLocation')}
                onSelectAll={onSelectAll}
                onToggleAllChildren={onToggleAllChildren}
                maxSelectableDepth={maxSelectableDepth}
                contentClassName={contentClassName}
                hasOldLocations={hasOldLocations}
                customSearchNoResultsText={customSearchNoResultsText}
              />
            )}
          </div>
        </>
      ) : (
        <div className='min-h-0 flex-1 overflow-y-auto'>
          <LocationSelectorContent
            searchQuery={searchQuery}
            multipleSelection={multipleSelection}
            locationsBlacklist={locationsBlacklist}
            allLocationsSelected={state.allLocationsSelected}
            allowedLocations={allowedNestableLocations}
            autoSelectChildren={state.autoSelectChildren}
            selectedLocations={selectedLocations}
            onSelect={(id) => onSelectLocation(id, 'NestableLocation')}
            onSelectAll={onSelectAll}
            onToggleAllChildren={onToggleAllChildren}
            maxSelectableDepth={maxSelectableDepth}
            contentClassName={contentClassName}
            hasOldLocations={hasOldLocations}
            customSearchNoResultsText={customSearchNoResultsText}
          />
        </div>
      )}
    </div>
  );
}
